import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Input,
  Button,
  Select,
  Popconfirm,
  Popover,
  Tag,
  Switch,
  Modal
} from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import Link from 'antd/es/typography/Link';
import { arrayMoveImmutable } from 'array-move';
import { SortableElement, SortableContainer, SortableHandle } from 'react-sortable-hoc';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';
import { userAtom, notificationsAtom } from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import envConfig from '../../../../envConfig';
import { Spinner } from '../../../shared-components/Spinner';
import { H1 } from '../../../shared-components/typography/Title';
import api from '../../../../api/api';
import { uuidv4 } from '../../../../functions';
import { fileColumnDataType } from '../../../../utils';

function AdminOrganizations() {
  const defaultFile = {
    ordering: 0,
    isActive: false
  };
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const user = useRecoilValue(userAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);

  function getOrganizations() {
    setLoading(true);
    api.get('organizations/all')
      .then((response) => {
        setLoading(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  function create() {
    setLoading(true);
    api.post('organizations', {
      NameEn: organizationData.nameEn,
      NameEs: organizationData.nameEs,
      IsActive: organizationData.isActive,
      ReferralCode: organizationData.referralCode,
      Logo: organizationData.logo,
      PrimaryColor: organizationData.primaryColor,
      SecondaryColor: organizationData.secondaryColor,
      DealPrefix: organizationData.dealPrefix,
      FromEmail: organizationData.fromEmail
    })
      .then((response) => {
        setLoading(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
          setShowModal(false);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function edit() {
    setLoading(true);
    api.put('organizations', {
      Id: organizationData.id,
      NameEn: organizationData.nameEn,
      NameEs: organizationData.nameEs,
      IsActive: organizationData.isActive,
      ReferralCode: organizationData.referralCode,
      Logo: organizationData.logo,
      PrimaryColor: organizationData.primaryColor,
      SecondaryColor: organizationData.secondaryColor,
      DealPrefix: organizationData.dealPrefix,
      FromEmail: organizationData.fromEmail
    })
      .then((response) => {
        setLoading(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
          setShowModal(false);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  function deleteOperator() {
    setLoading(true);
    api.delete(`organizations/${organizationData?.id}`)
      .then((response) => {
        setLoading(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
          setShowModal(false);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      });
  }

  useEffect(() => {
    getOrganizations();
  }, []);
  
  return (
    <MainPage includeBack requiresCloserAdmin>
      <div className="flex mt-4">
        <H1 text={t('organizations')} />
        <div className="flex ml-auto space-x-2">
          <Button
            key="create"
            size="small"
            type="primary"
            onClick={() => {
              setOrganizationData(null);
              setShowModal(true);
            }}
          >
            {t('create')}
          </Button>
          <Button
            key="reload"
            size="small"
            type="primary"
            onClick={() => getOrganizations()}
          >
            {t('reload')}
          </Button>
        </div>
      </div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        className="mt-4"
        dataSource={organizations}
        bordered
        loading={loading}
        rowKey="id"
        pagination={false}
        columns={[
          {
            title: t('active'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.isActive ? <Tag color="green">{row.nameEn}</Tag> : <Tag color="red">{row.nameEn}</Tag>}</span>
            ),
          },
          {
            title: t('referral_code'),
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <span>{row.referralCode}</span>
            ),
          },
          {
            title: '',
            align: 'left',
            className: 'text-sm',
            render: (row) => (
              <div className="space-x-2 flex">
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setOrganizationData(row);
                    setShowModal(true);
                  }}
                >
                  {t('edit')}
                </Button>
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    history.push(`/admin/leads/${row.referralCode}`);
                  }}
                >
                  {t('leads')}
                </Button>
              </div>
            ),
          }
        ]}
      />
      <Modal
        title={organizationData?.id ? t('edit_organization') : t('create_organization')}
        open={showModal}
        onCancel={() => {
          setOrganizationData(null);
          setShowModal(false);
        }}
        footer={[
          <div className="flex space-x-2">
            <div className="mr-auto" />
            {organizationData?.id && (
              <Popconfirm
                key="closepopup"
                title={t('delete_organizations_prompt')}
                okText={t('yes')}
                cancelText={t('no')}
                onConfirm={() => {
                  deleteOperator();
                }}
              >
                <Button
                  key="close"
                  type="primary"
                  danger
                  loading={loading}
                >
                  {t('delete')}
                </Button>
              </Popconfirm>
            )}
            <Button
              key="close"
              type="primary"
              danger
              loading={loading}
              onClick={() => {
                setOrganizationData(null);
                setShowModal(false);
              }}
            >
              {t('close')}
            </Button>
            <Button
              key="ok"
              type="primary"
              disabled={!(organizationData?.nameEn?.length > 0) || !(organizationData?.nameEs?.length > 0)}
              loading={loading}
              onClick={() => {
                if (organizationData?.id) {
                  edit();
                } else {
                  create();
                }
              }}
            >
              {t('submit')}
            </Button>
          </div>
        ]}
      >
        <div className="space-y-2">
          <div className="mt-4">
            <strong>{t('active')}</strong>
            <Switch
              disabled={loading}
              style={{ float: 'right' }}
              checked={organizationData?.isActive}
              onChange={(checked) => {
                setOrganizationData({
                  ...organizationData,
                  isActive: checked
                });
              }}
            />
          </div>
          <div>
            <strong>{t('name_en')} </strong>
            <Input
              disabled={loading}
              value={organizationData?.nameEn}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  nameEn: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>{t('name_es')}</strong>
            <Input
              disabled={loading}
              value={organizationData?.nameEs}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  nameEs: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>
              {t('referral_code')}
              <Popover content={t('referral_code_hint')}>
                <FontAwesomeIcon className="ml-1 text-primaryColor" icon={faInfoCircle} />
              </Popover>
            </strong>
            <Input
              disabled={loading}
              value={organizationData?.referralCode}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  referralCode: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>
              {t('from_email')}
              <Popover content={t('from_email_hint')}>
                <FontAwesomeIcon className="ml-1 text-primaryColor" icon={faInfoCircle} />
              </Popover>
            </strong>
            <Input
              disabled={loading}
              value={organizationData?.fromEmail}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  fromEmail: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>{t('logo')}</strong>
            <Input
              disabled={loading}
              value={organizationData?.logo}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  logo: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>{t('primary_color')}</strong>
            <Input
              disabled={loading}
              value={organizationData?.primaryColor}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  primaryColor: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>{t('secondary_color')}</strong>
            <Input
              disabled={loading}
              value={organizationData?.secondaryColor}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  secondaryColor: obj.target.value
                });
              }}
            />
          </div>
          <div>
            <strong>
              {t('deal_prefix')}
              <Popover content={t('deal_prefix_hint')}>
                <FontAwesomeIcon className="ml-1 text-primaryColor" icon={faInfoCircle} />
              </Popover>
            </strong>
            <Input
              disabled={loading}
              value={organizationData?.dealPrefix}
              onChange={(obj) => {
                setOrganizationData({
                  ...organizationData,
                  dealPrefix: obj.target.value
                });
              }}
            />
          </div>
        </div>
      </Modal>
    </MainPage>
  );
}

export default AdminOrganizations;
